import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { t } from "i18next";
import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Spinner,
  Input,
  Row,
  Col,
  Card,
  CardBody,
} from "reactstrap";
import { useSpacePopupStore } from "../../../../stores/Assets/spacePopup";
import { useSpaceStore } from "../../../../stores/Assets/space";
import { useSiteStore } from "../../../../stores/Assets/site";
import { FormControlLabel, Switch } from "@mui/material";
import Select from "react-select";

// Helper function to convert UTC date to local timezone for datetime-local input
const utcToLocalDateTime = (utcDateString) => {
  if (!utcDateString) return "";
  const date = new Date(utcDateString);
  // Get local date components
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Helper function to convert local datetime to UTC ISO string
const localDateTimeToUTC = (localDateTimeString) => {
  if (!localDateTimeString) return undefined;
  // Create a date object from the local datetime string
  // This will be interpreted as local time
  const localDate = new Date(localDateTimeString);
  // Convert to ISO string (UTC)
  return localDate.toISOString();
};

const EditAnnouncement = ({
  rowData,
  showEditAnnouncementModal,
  toggleEditAnnouncementModal,
}) => {
  const { updateSpacePopup, isLoading, getSpacePopup } = useSpacePopupStore((state) => state);
  const { getSpaces } = useSpaceStore((state) => state);
  const { getSites, sites } = useSiteStore((state) => state);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [previewLanguage, setPreviewLanguage] = useState("EN"); // EN or AR
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [allSpaces, setAllSpaces] = useState([]);
  const [selectedSpaceIds, setSelectedSpaceIds] = useState([]);
  const [popupData, setPopupData] = useState(null);

  // Fetch popup data with linked spaces
  useEffect(() => {
    const fetchPopupData = async () => {
      if (rowData?.id) {
        const data = await getSpacePopup(rowData.id);
        setPopupData(data);
        // Get linked spaces
        if (data?.spaces && data.spaces.length > 0) {
          const linkedSpaceIds = data.spaces.map((s) => s.space_id);
          setSelectedSpaceIds(linkedSpaceIds);
          // Set site from first linked space (spaces will be filtered by site when loaded)
          if (data.spaces[0]?.site_id) {
            setSelectedSiteId(data.spaces[0].site_id);
          }
        }
      }
    };
    fetchPopupData();
  }, [rowData]);

  // Fetch sites on mount
  useEffect(() => {
    const fetchSites = async () => {
      await getSites();
    };
    fetchSites();
  }, []);

  // Fetch spaces when site is selected
  useEffect(() => {
    const fetchSpaces = async () => {
      if (selectedSiteId) {
        await getSpaces(selectedSiteId, { page: 1, perPage: 500, search: "" });
      }
    };
    fetchSpaces();
  }, [selectedSiteId]);

  // Update allSpaces when spaces change
  const spaces = useSpaceStore((state) => state.spaces);
  useEffect(() => {
    if (spaces) {
      setAllSpaces(spaces);
    }
  }, [spaces]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: rowData?.title || "",
      title_ar: rowData?.title_ar || "",
      description: rowData?.description || "",
      description_ar: rowData?.description_ar || "",
      image: rowData?.image || "",
      starts_at: utcToLocalDateTime(rowData?.starts_at),
      expires_at: utcToLocalDateTime(rowData?.expires_at),
      see_once: rowData?.see_once || false,
    },
    validationSchema: Yup.object({
      title: Yup.string().optional(),
      title_ar: Yup.string().optional(),
      description: Yup.string().optional(),
      description_ar: Yup.string().optional(),
      image: Yup.string().optional(),
      starts_at: Yup.string().optional(),
      expires_at: Yup.string().optional(),
      see_once: Yup.boolean().optional(),
    }),
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append("title", values.title);
      if (values.title_ar) formData.append("title_ar", values.title_ar);
      if (values.description) formData.append("description", values.description);
      if (values.description_ar) formData.append("description_ar", values.description_ar);
      if (values.starts_at) formData.append("starts_at", localDateTimeToUTC(values.starts_at));
      if (values.expires_at) formData.append("expires_at", localDateTimeToUTC(values.expires_at));
      formData.append("see_once", values.see_once.toString());
      
      // Add image file if provided
      if (imageFile) {
        formData.append("file", imageFile);
      }

      // Add space IDs as JSON string
      formData.append("spaceIds", JSON.stringify(selectedSpaceIds));

      const result = await updateSpacePopup(rowData.id, formData);
      if (result) {
        toggleEditAnnouncementModal();
      }
    },
  });

  useEffect(() => {
    if (rowData) {
      if (rowData?.image) {
        setImagePreview(rowData.image);
      } else {
        setImagePreview(null);
      }
    }
  }, [rowData]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setImagePreview(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  // Get preview data based on selected language
  const getPreviewData = () => {
    if (previewLanguage === "AR") {
      return {
        title: formik.values.title_ar || formik.values.title,
        description: formik.values.description_ar || formik.values.description,
      };
    }
    return {
      title: formik.values.title,
      description: formik.values.description,
    };
  };

  const previewData = getPreviewData();

  return (
    <Modal isOpen={showEditAnnouncementModal} toggle={toggleEditAnnouncementModal} size="lg">
      <ModalHeader toggle={toggleEditAnnouncementModal}>
        {t("Edit Announcement")}: {rowData?.title || rowData?.id}
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col md={6}>
            <form onSubmit={formik.handleSubmit}>
              <div className="mb-3">
                <Label for="title">{t("Title")} *</Label>
                <Input
                  type="text"
                  id="title"
                  name="title"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.title && !!formik.errors.title}
                />
                {formik.touched.title && formik.errors.title && (
                  <div className="text-danger small">{formik.errors.title}</div>
                )}
              </div>

              <div className="mb-3">
                <Label for="title_ar">{t("Title (AR)")}</Label>
                <Input
                  type="text"
                  id="title_ar"
                  name="title_ar"
                  value={formik.values.title_ar}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>

              <div className="mb-3">
                <Label for="description">{t("Subtitle")}</Label>
                <Input
                  type="textarea"
                  id="description"
                  name="description"
                  rows="4"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>

              <div className="mb-3">
                <Label for="description_ar">{t("Subtitle (AR)")}</Label>
                <Input
                  type="textarea"
                  id="description_ar"
                  name="description_ar"
                  rows="4"
                  value={formik.values.description_ar}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>

              <div className="mb-3">
                <Label for="imageFile">{t("Image")}</Label>
                {imagePreview && (
                  <div className="mb-2">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{ maxWidth: "200px", maxHeight: "200px", objectFit: "cover", borderRadius: "4px" }}
                    />
                  </div>
                )}
                <Input
                  type="file"
                  id="imageFile"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                <small className="text-muted">{t("Leave empty to keep current image")}</small>
              </div>

              <div className="mb-3">
                <Label for="site">{t("Select Site")}</Label>
                <select
                  id="site"
                  className="form-control"
                  value={selectedSiteId || ""}
                  onChange={(e) => {
                    const newSiteId = e.target.value ? parseInt(e.target.value) : null;
                    setSelectedSiteId(newSiteId);
                    // When site changes, preserve linked spaces if they belong to the new site
                    // Otherwise clear selections (they'll be re-selected when spaces load if they're linked)
                    if (newSiteId && popupData?.spaces) {
                      const linkedSpaceIds = popupData.spaces
                        .filter((s) => s.site_id === newSiteId)
                        .map((s) => s.space_id);
                      setSelectedSpaceIds(linkedSpaceIds);
                    } else {
                      // Keep current selections if they're valid, otherwise clear
                      const validSelections = selectedSpaceIds.filter((id) => 
                        popupData?.spaces?.some((s) => s.space_id === id)
                      );
                      setSelectedSpaceIds(validSelections.length > 0 ? validSelections : []);
                    }
                  }}
                >
                  <option value="">{t("Select a site")}</option>
                  {sites?.map((site) => (
                    <option key={site.site_id} value={site.site_id}>
                      {site.site_id} - {site.name}
                    </option>
                  ))}
                </select>
              </div>

              {selectedSiteId && (
                <div className="mb-3">
                  <Label for="spaces">
                    {t("Select Spaces")} 
                    {selectedSpaceIds.length > 0 && (
                      <span className="text-muted ms-2">
                        ({selectedSpaceIds.length} {t("selected")})
                      </span>
                    )}
                  </Label>
                  <Select
                    id="spaces"
                    isMulti
                    options={allSpaces?.map((space) => ({
                      value: space.space_id,
                      label: `${space.space_id} - ${space.name}`,
                    }))}
                    value={allSpaces
                      ?.filter((space) => selectedSpaceIds.includes(space.space_id))
                      .map((space) => ({
                        value: space.space_id,
                        label: `${space.space_id} - ${space.name}`,
                      }))}
                    onChange={(selectedOptions) => {
                      const ids = selectedOptions
                        ? selectedOptions.map((option) => option.value)
                        : [];
                      setSelectedSpaceIds(ids);
                    }}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    placeholder={t("Select one or more spaces...")}
                    isClearable
                    isSearchable
                  />
                  {selectedSpaceIds.length > 0 && (
                    <small className="text-muted d-block mt-1">
                      {t("You can select multiple spaces by clicking on them")}
                    </small>
                  )}
                </div>
              )}

              <div className="mb-3">
                <Label for="starts_at">{t("Starts At")}</Label>
                <Input
                  type="datetime-local"
                  id="starts_at"
                  name="starts_at"
                  value={formik.values.starts_at}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>

              <div className="mb-3">
                <Label for="expires_at">{t("Expires At")}</Label>
                <Input
                  type="datetime-local"
                  id="expires_at"
                  name="expires_at"
                  value={formik.values.expires_at}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>

              <div className="mb-3">
                <FormControlLabel
                  control={
                    <Switch
                      checked={formik.values.see_once}
                      onChange={(e) =>
                        formik.setFieldValue("see_once", e.target.checked)
                      }
                />
                  }
                  label={t("See Once")}
                />
              </div>

              <div className="d-flex gap-2 mt-4">
                <Button type="submit" color="success" disabled={isLoading}>
                  {isLoading ? <Spinner size="sm" /> : t("Update")}
                </Button>
                <Button
                  color="danger"
                  onClick={() => {
                    toggleEditAnnouncementModal();
                  }}
                  disabled={isLoading}
                >
                  {t("Cancel")}
                </Button>
              </div>
            </form>
          </Col>

          <Col md={6}>
            <div className="sticky-top" style={{ top: "20px" }}>
              <Label className="mb-2">
                <strong>{t("Preview")}</strong>
              </Label>
              <div className="mb-2">
                <Button
                  size="sm"
                  outline
                  color={previewLanguage === "EN" ? "primary" : "secondary"}
                  onClick={() => setPreviewLanguage("EN")}
                  className="me-2"
                >
                  EN
                </Button>
                <Button
                  size="sm"
                  outline
                  color={previewLanguage === "AR" ? "primary" : "secondary"}
                  onClick={() => setPreviewLanguage("AR")}
                >
                  AR
                </Button>
              </div>

              {/* Mobile Preview */}
              <div
                style={{
                  maxWidth: "375px",
                  margin: "0 auto",
                  position: "relative",
                  backgroundColor: "#2a2a2a", // Dark grey background
                  borderRadius: "16px",
                  padding: "20px",
                  minHeight: "500px",
                }}
              >
                {/* White Popup Card */}
                <Card
                  style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                    position: "relative",
                  }}
                >
                  {/* Close Button - Top Right */}
                  <button
                    onClick={() => {}}
                    style={{
                      position: "absolute",
                      top: "12px",
                      right: "12px",
                      width: "32px",
                      height: "32px",
                      border: "none",
                      backgroundColor: "transparent",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 10,
                      padding: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: "18px",
                        fontWeight: "bold",
                        color: "#000",
                        lineHeight: "1",
                      }}
                    >
                      ×
                    </span>
                  </button>

                  <CardBody style={{ padding: "24px 20px 20px 20px" }}>
                    {/* Centered Header Text */}
                    <div
                      style={{
                        textAlign: "center",
                        marginBottom: "20px",
                        direction: previewLanguage === "AR" ? "rtl" : "ltr",
                      }}
                    >
                      <h5
                        style={{
                          marginBottom: "8px",
                          fontSize: "22px",
                          fontWeight: "700",
                          color: "#000",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {previewData?.title}
                      </h5>
                      <p
                        style={{
                          marginBottom: 0,
                          fontSize: "16px",
                          color: "#000",
                          fontWeight: "400",
                        }}
                      >
                        {previewData?.description.split('\n')[0] }
                      </p>
                    </div>

                    {/* Large Image */}
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        marginBottom: "20px",
                        borderRadius: "8px",
                        overflow: "hidden",
                        backgroundColor: "#f0f0f0",
                        minHeight: "250px",
                      }}
                    >
                      {imagePreview ? (
                        <img
                          src={imagePreview}
                          alt="Announcement"
                          style={{
                            width: "100%",
                            height: "250px",
                            display: "block",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            color: "#999",
                            fontSize: "14px",
                          }}
                        >
                          {t("No Image")}
                        </div>
                      )}
                    </div>

               
                  </CardBody>
                </Card>
              </div>
            </div>
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  );
};

export default EditAnnouncement;

